import CopyLinkButton from '@/components/Discord/CopyLinkButton'
import { useState } from 'react'
import GuidelineTooltip from '../../../components/Discord/GuidelineTooltip'
import styles from '@/components/Discord/DiscordDocument.module.css'
import DiscordDocument from '@/components/Discord/DiscordDocument'

function DiscordIdentityAuthenticityPolicyPage() {
  const [language, setLanguage] = useState<'en' | 'cs'>('cs')
  const [banEvasionExamplesOpen, setBanEvasionExamplesOpen] = useState(false)

  return (
    <DiscordDocument
      language={language}
      onLanguageChange={setLanguage}
      heading={
        <div className={styles.titleSection}>
          <h1 className={styles.title}>
            {language === 'cs'
              ? 'Politika identity a autenticity'
              : 'Identity and Authenticity Policy'}
          </h1>
        </div>
      }
      notice={
        <>
          <div className={styles.disclaimer}>
            <div className={styles.disclaimerCard} lang="cs">
              <h3>Důležité upozornění</h3>
              <p>
                Nejsme přidruženi ani spojeni s Discord Inc. Toto je pouze komunitní překlad
                oficiální stránky o politice identity a autenticity Discord dostupné na{' '}
                <a
                  href="https://discord.com/safety/identity-authenticity-policy-explainer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  discord.com/safety/identity-authenticity-policy-explainer
                </a>
                .
              </p>
              <p>
                Tento překlad nemusí být aktuální s oficiální politikou. Pokud si všimnete rozdílu
                mezi oficiální stránkou a touto stránkou, prosím, dejte nám vědět na našem Discord
                serveru.
              </p>
            </div>
            <div className={styles.disclaimerCard} lang="en">
              <h3>Important Notice</h3>
              <p>
                We are not affiliated with or associated with Discord Inc. This is only a community
                translation of the official Discord identity and authenticity policy available at{' '}
                <a
                  href="https://discord.com/safety/identity-authenticity-policy-explainer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  discord.com/safety/identity-authenticity-policy-explainer
                </a>
                .
              </p>
              <p>
                This translation may not be up to date with the official policy. If you notice a
                difference between the official page and this page, please let us know on our
                Discord server.
              </p>
            </div>
          </div>
          <div className={styles.linkLegend}>
            <h4>{language === 'cs' ? 'Legenda:' : 'Legend:'}</h4>
            <div className={styles.legendItem}>
              <span className={`${styles.legendLink} ${styles.discord}`}>
                {language === 'cs' ? 'Modré odkazy' : 'Blue links'}
              </span>
              <span>
                = {language === 'cs' ? 'oficiální Discord stránky' : 'official Discord pages'}
              </span>
            </div>
            <div className={styles.legendItem}>
              <span className={`${styles.legendLink} ${styles.internal}`}>
                {language === 'cs' ? 'Žluté odkazy' : 'Yellow links'}
              </span>
              <span>= {language === 'cs' ? 'naše přeložené stránky' : 'our translated pages'}</span>
            </div>
          </div>
        </>
      }
    >
      {/* Quick Summary - Czech only */}
      {language === 'cs' && (
        <details className={styles.quickSummary} id="quick-summary">
          <summary>
            <h2>Rychlé shrnutí politiky</h2>
          </summary>
          <CopyLinkButton
            sectionId={'quick-summary'}
            language={language}
            className={styles.copyBtn}
          />

          <div className={styles.summaryGrid}>
            <div className={styles.summaryCard}>
              <h3>Zkreslování identity</h3>
              <ul>
                <li>Žádné falešné profily pro neexistující osoby</li>
                <li>Zákaz vydávání se za jiné osoby nebo organizace</li>
                <li>Pseudonymní účty jsou povoleny</li>
                <li>Parodie musí být jasně označeny</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Obcházení banu</h3>
              <ul>
                <li>Respektujte trvalé bany účtů</li>
                <li>Nevytvářejte nové účty po banu</li>
                <li>Neobnovujte zakázané servery</li>
                <li>Můžete podat odvolání, pokud nesouhlasíte</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Jak se chránit</h3>
              <ul>
                <li>Buďte autentičtí ve své identitě</li>
                <li>Jasně označte parodie a satiru</li>
                <li>Respektujte rozhodnutí platformy</li>
                <li>Opusťte servery porušující pravidla</li>
              </ul>
            </div>
          </div>
        </details>
      )}

      {/* Policy Body */}
      <div className={styles.policyBody}>
        {language === 'cs' ? (
          <>
            <div className={styles.policySection}>
              <div id="identity-misrepresentation" className={styles.sectionWrapper}>
                <CopyLinkButton
                  sectionId={'identity-misrepresentation'}
                  language={language}
                  className={styles.copyBtn}
                />
                <div className={styles.sectionContent}>
                  <h2>
                    <strong>Zkreslování identity</strong>
                  </h2>
                  <p>
                    Discord je místo, kde se lidé scházejí s přáteli a budují komunity. Důležitou
                    podmínkou pro budování zdravých komunit je důvěra v autenticitu interakcí.
                    Discord zakazuje uživatelům zkreslit svou identitu na naší platformě klamavým
                    nebo škodlivým způsobem. To zahrnuje vytváření falešných profilů, stejně jako
                    pokusy o vydávání se za jednotlivce, skupinu nebo organizaci.
                  </p>
                  <p>
                    Ačkoli jsou na Discordu povoleny pseudonymní účty, naše pravidla zakazují
                    falešné profily a vydávání se za někoho jiného. <strong>Falešný profil</strong>{' '}
                    definujeme jako online identitu pro jednotlivce nebo entitu, která neexistuje.
                    Některé falešné uživatelské profily lze identifikovat podle jejich používání
                    ukradených nebo vygenerovaných obrázků jako avatarů.{' '}
                    <strong>Vydávání se za někoho jiného</strong>
                    znamená předstírat, že jste skutečný jednotlivec, skupina nebo organizace. To
                    obvykle zahrnuje použití podrobností o identitě (jako je jméno nebo podoba) k
                    oklamání ostatních o vlastnictví nebo přidružení digitálního profilu. Vydávání
                    se za někoho jiného na Discordu může nastat na úrovni účtu nebo serveru.
                  </p>
                </div>
              </div>
            </div>

            <div className={styles.policySection}>
              <div id="platform-ban-evasion" className={styles.sectionWrapper}>
                <CopyLinkButton
                  sectionId={'platform-ban-evasion'}
                  language={language}
                  className={styles.copyBtn}
                />
                <div className={styles.sectionContent}>
                  <h2>
                    <strong>Obcházení banu platformy</strong>
                  </h2>
                  <p>
                    Můžeme podniknout donucovací opatření, když zjistíme porušení našich{' '}
                    <a href="/discord/guidelines">Pokynů komunity</a> — až po trvalé odstranění z
                    Discordu. Trvalé akce, jako jsou bany uživatelů a serverů, si obvykle
                    vyhrazujeme pro závažná porušení, která by mohla negativně ovlivnit bezpečnost
                    uživatelů, integritu naší služby nebo práva ostatních. Trvalé odstranění
                    porušujícího účtu nebo komunity může zmírnit škody způsobené těmito skupinami a
                    pomoci předcházet zneužívání v budoucnu.
                  </p>
                  <p>
                    Nesmíte obcházet bany na úrovni platformy, které proti vašemu účtu nebo serveru
                    podnikneme. Termín
                    <strong> ban</strong> používáme k označení trvalého odstranění účtu nebo
                    komunity za porušení našich pravidel.
                  </p>
                  <p>
                    <strong>Obcházení banu platformy</strong> se týká snah o návrat na Discord po
                    trvalém odstranění účtu nebo komunity z naší služby. Obcházení banu platformy
                    zahrnuje vytváření nových účtů a komunit k obejití trvalého odstranění; zahrnuje
                    také použití nebo opětovné použití existujícího účtu nebo komunity k vyhnutí se
                    banu na úrovni platformy.
                  </p>
                </div>
              </div>

              <div className={styles.examplesSection}>
                <button
                  className={styles.dropdownToggle}
                  onClick={() => setBanEvasionExamplesOpen(!banEvasionExamplesOpen)}
                  aria-expanded={banEvasionExamplesOpen}
                  type="button"
                >
                  <span>Například nesmíte zveřejňovat, sdílet nebo se zapojovat do:</span>
                  <span className={styles.dropdownIcon}>{banEvasionExamplesOpen ? '−' : '+'}</span>
                </button>

                {banEvasionExamplesOpen && (
                  <div className={styles.dropdownContent}>
                    <ul>
                      <li>Vytváření nového účtu po trvalém zablokování z Discordu</li>
                      <li>
                        Znovuvytvoření serveru, který byl odstraněn z Discordu oddělením Trust &
                        Safety
                      </li>
                      <li>
                        Přeměna existujícího účtu nebo serveru za účelem vyhnutí se zablokování
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.policySection}>
              <div id="how-to-avoid" className={styles.sectionWrapper}>
                <CopyLinkButton
                  sectionId={'how-to-avoid'}
                  language={language}
                  className={styles.copyBtn}
                />
                <div className={styles.sectionContent}>
                  <h2>
                    <strong>Jak se vyhnout porušení těchto politik</strong>
                  </h2>
                  <ul>
                    <li>Nezkreslujte svou identitu na Discordu klamavým nebo škodlivým způsobem</li>
                    <li>
                      Pokud provozujete parodický nebo satirický účet nebo server, ujistěte se, že
                      je jasně označen jako takový
                    </li>
                    <li>
                      Respektujte donucovací opatření na úrovni platformy. Jednotlivci, kteří se
                      domnívají, že jejich účet nebo server byl nesprávně odstraněn, najdou postup
                      odvolání{' '}
                      <a
                        href="https://discord.com/safety/360043712172-how-you-can-appeal-our-actions"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        zde
                      </a>
                    </li>
                  </ul>
                  <p>
                    Nahlaste a opusťte servery věnované jakémukoli z výše popsaného porušujícího
                    obsahu. Discord může také podniknout kroky proti účtům, které jsou přítomny na
                    porušujících serverech.
                  </p>
                </div>
              </div>
            </div>

            <div className={styles.policySection}>
              <div id="how-to-report" className={styles.sectionWrapper}>
                <CopyLinkButton
                  sectionId={'how-to-report'}
                  language={language}
                  className={styles.copyBtn}
                />
                <div className={styles.sectionContent}>
                  <h2>
                    <strong>Jak nahlásit porušení těchto politik</strong>
                  </h2>
                  <p>
                    Nahlašování porušení bezpečnosti je kriticky důležité pro udržení bezpečnosti
                    vás a širší komunity Discord. Všichni uživatelé Discordu mohou nahlásit porušení
                    politik v aplikaci podle pokynů{' '}
                    <a href="/discord/safety/reporting-abusive-behavior-to-discord">zde</a>. Pro
                    více informací o těchto politikách se prosím podívejte na naše{' '}
                    <a href="/discord/guidelines">Pokyny komunity</a>{' '}
                    <GuidelineTooltip guidelineNumber="18" language={language}>
                      <a href="/discord/guidelines#guideline-18" className={styles.guidelineLink}>
                        #18
                      </a>
                    </GuidelineTooltip>
                    {' a '}
                    <GuidelineTooltip guidelineNumber="19" language={language}>
                      <a href="/discord/guidelines#guideline-19" className={styles.guidelineLink}>
                        #19
                      </a>
                    </GuidelineTooltip>
                    .
                  </p>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={styles.policySection}>
              <div id="identity-misrepresentation" className={styles.sectionWrapper}>
                <CopyLinkButton
                  sectionId={'identity-misrepresentation'}
                  language={language}
                  className={styles.copyBtn}
                />
                <div className={styles.sectionContent}>
                  <h2>
                    <strong>Identity Misrepresentation</strong>
                  </h2>
                  <p>
                    Discord is a place where people come to hang out with friends and build
                    communities. An important condition for building healthy communities is trust in
                    the authenticity of interactions. Discord prohibits users from misrepresenting
                    their identity on our platform in a deceptive or harmful way. This includes
                    creating fake profiles as well as attempts to impersonate an individual, group,
                    or organization.
                  </p>
                  <p>
                    Although pseudonymous accounts are allowed on Discord, our rules prohibit fake
                    profiles and impersonation. We define a <strong>fake profile</strong> as an
                    online identity for an individual or entity that does not exist. Some fake user
                    profiles can be identified by their use of stolen or generated images as
                    avatars. <strong>Impersonation</strong> means pretending to be a real
                    individual, group, or organization. This typically involves the use of identity
                    details (such as name or likeness) to deceive others about the ownership or
                    affiliation of a digital profile. Impersonation on Discord can occur at the
                    account or server level.
                  </p>
                </div>
              </div>
            </div>

            <div className={styles.policySection}>
              <div id="platform-ban-evasion" className={styles.sectionWrapper}>
                <CopyLinkButton
                  sectionId={'platform-ban-evasion'}
                  language={language}
                  className={styles.copyBtn}
                />
                <div className={styles.sectionContent}>
                  <h2>
                    <strong>Platform Ban Evasion</strong>
                  </h2>
                  <p>
                    We may take enforcement actions when we discover a violation of our{' '}
                    <a href="/discord/guidelines">Community Guidelines</a> — up to and including
                    permanent removal from Discord. We typically reserve permanent actions like user
                    and server bans for severe violations that could negatively impact user safety,
                    the integrity of our service, or the rights of others. Permanently removing a
                    violative account or community can lessen the harms from these groups and help
                    prevent abuse from occurring in the future.
                  </p>
                  <p>
                    You may not circumvent platform-level bans that we take against your account or
                    server. We use the term <strong>ban</strong> to refer to the permanent removal
                    of an account or community for a violation of our rules.
                  </p>
                  <p>
                    <strong>Platform ban evasion</strong> refers to efforts to return to Discord
                    after having an account or community permanently removed from our service.
                    Platform ban evasion includes the creation of new accounts and communities to
                    bypass permanent removal; it also includes the use or reuse of an existing
                    account or community to avoid a platform-level ban.
                  </p>
                </div>
              </div>

              <div className={styles.examplesSection}>
                <button
                  className={styles.dropdownToggle}
                  onClick={() => setBanEvasionExamplesOpen(!banEvasionExamplesOpen)}
                  aria-expanded={banEvasionExamplesOpen}
                  type="button"
                >
                  <span>For example, you may not post, share, or engage in:</span>
                  <span className={styles.dropdownIcon}>{banEvasionExamplesOpen ? '−' : '+'}</span>
                </button>

                {banEvasionExamplesOpen && (
                  <div className={styles.dropdownContent}>
                    <ul>
                      <li>Creating a new account after being permanently banned from Discord</li>
                      <li>
                        Recreating a server that has been removed from Discord by Trust & Safety
                      </li>
                      <li>Repurposing an existing account or server to evade being banned</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.policySection}>
              <div id="how-to-avoid" className={styles.sectionWrapper}>
                <CopyLinkButton
                  sectionId={'how-to-avoid'}
                  language={language}
                  className={styles.copyBtn}
                />
                <div className={styles.sectionContent}>
                  <h2>
                    <strong>How to Avoid Violating These Policies</strong>
                  </h2>
                  <ul>
                    <li>
                      Don't misrepresent your identity on Discord in a deceptive or harmful way
                    </li>
                    <li>
                      If you run a parody or satire account or server, make sure that it is clearly
                      labeled as such
                    </li>
                    <li>
                      Respect platform-level enforcement actions. Individuals who believe their
                      account or server has been incorrectly removed can find the appeal process{' '}
                      <a
                        href="https://discord.com/safety/360043712172-how-you-can-appeal-our-actions"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        here
                      </a>
                    </li>
                  </ul>
                  <p>
                    Please report and leave servers dedicated to any of the violative content
                    described above. Discord may also take action against accounts that are present
                    in violative servers.
                  </p>
                </div>
              </div>
            </div>

            <div className={styles.policySection}>
              <div id="how-to-report" className={styles.sectionWrapper}>
                <CopyLinkButton
                  sectionId={'how-to-report'}
                  language={language}
                  className={styles.copyBtn}
                />
                <div className={styles.sectionContent}>
                  <h2>
                    <strong>How to Report Violations of These Policies</strong>
                  </h2>
                  <p>
                    Reporting safety violations is critically important to keeping you and the
                    broader Discord community safe. All Discord users can report policy violations
                    right in the app by following the instructions{' '}
                    <a href="/discord/safety/reporting-abusive-behavior-to-discord">here</a>. For
                    more information on these policies, please reference our{' '}
                    <a href="/discord/guidelines">Community Guidelines</a>{' '}
                    <GuidelineTooltip guidelineNumber="18" language={language}>
                      <a href="/discord/guidelines#guideline-18" className={styles.guidelineLink}>
                        #18
                      </a>
                    </GuidelineTooltip>
                    {' and '}
                    <GuidelineTooltip guidelineNumber="19" language={language}>
                      <a href="/discord/guidelines#guideline-19" className={styles.guidelineLink}>
                        #19
                      </a>
                    </GuidelineTooltip>
                    .
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Tags section */}
      <div className={styles.tags}>
        <div className={styles.tagsLabel}>Tags</div>
        <div className={styles.tagsList}>
          <div className={styles.tag}>
            {language === 'cs' ? 'Integrita platformy' : 'Platform Integrity'}
          </div>
        </div>
      </div>
    </DiscordDocument>
  )
}

export default DiscordIdentityAuthenticityPolicyPage
